import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Accordion,
    AccordionItem,
    AccordionPanel,
    AccordionButton,
    useMultiStyleConfig,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {defineMessages, useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/* Components */
import Link from '@salesforce/retail-react-app/app/components/link'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

/* Icons */
import {AccordionArrow} from '../../icons/index'

/* Utils */
import {generateStyles} from '../../optimizely/utils/utils'
import {sendClickEvent} from '../../../utils/gtm-utils'

/* Contexts */
import {useGetLinkData} from '../../../contexts/category-url-context'

/* Global Constants */
const messages = defineMessages({
    // About Us section
    aboutUs: {
        id: 'footer.aboutUs',
        defaultMessage: 'About Us'
    },
    aboutGarage: {
        id: 'footer.aboutGarage',
        defaultMessage: 'About Garage'
    },
    storeLocator: {
        id: 'footer.storeLocator',
        defaultMessage: 'Store Locator'
    },
    careers: {
        id: 'footer.careers',
        defaultMessage: 'Careers'
    },
    communityPage: {
        id: 'footer.communityPage',
        defaultMessage: 'Community Page'
    },
    cookiePreferences: {
        id: 'footer.cookiePreferences',
        defaultMessage: 'Cookie Preferences'
    },
    affiliateProgram: {
        id: 'footer.affiliateProgram',
        defaultMessage: 'Affiliate Program'
    },
    gscAmbassadorProgram: {
        id: 'footer.gscAmbassadorProgram',
        defaultMessage: 'GSC Ambassador Program'
    },
    loyaltyProgram: {
        id: 'footer.loyaltyProgram',
        defaultMessage: 'Loyalty Program'
    },
    // Customer Service section
    customerService: {
        id: 'footer.customerService',
        defaultMessage: 'Customer Service'
    },
    contactUs: {
        id: 'footer.contactUs',
        defaultMessage: 'Contact Us'
    },
    orderTracking: {
        id: 'footer.orderTracking',
        defaultMessage: 'Order Tracking'
    },
    faq: {
        id: 'footer.faq',
        defaultMessage: 'FAQ'
    },
    shippingDelivery: {
        id: 'footer.shippingDelivery',
        defaultMessage: 'Shipping & Delivery'
    },
    returnPolicy: {
        id: 'footer.returnPolicy',
        defaultMessage: 'Return Policy'
    },
    sizeGuide: {
        id: 'footer.sizeGuide',
        defaultMessage: 'Size Guide'
    },
    orderHistory: {
        id: 'footer.orderHistory',
        defaultMessage: 'Order History'
    },
    buyGiftCard: {
        id: 'footer.buyGiftCard',
        defaultMessage: 'Buy a Gift Card'
    },
    giftCardBalance: {
        id: 'footer.giftCardBalance',
        defaultMessage: 'Gift Card Balance'
    },
    paymentInfo: {
        id: 'footer.paymentInfo',
        defaultMessage: 'Payment Info'
    },
    klarna: {
        id: 'footer.klarna',
        defaultMessage: 'Klarna'
    },
    // Socials section
    socials: {
        id: 'footer.socials',
        defaultMessage: 'Socials'
    },
    tiktok: {
        id: 'footer.tiktok',
        defaultMessage: 'Tiktok'
    },
    instagram: {
        id: 'footer.instagram',
        defaultMessage: 'Instagram'
    },
    facebook: {
        id: 'footer.facebook',
        defaultMessage: 'Facebook'
    },
    pinterest: {
        id: 'footer.pinterest',
        defaultMessage: 'Pinterest'
    },
    // Legals section
    legals: {
        id: 'footer.legals',
        defaultMessage: 'Legals'
    },
    termsConditions: {
        id: 'footer.termsConditions',
        defaultMessage: 'Terms & Conditions'
    },
    privacyPolicy: {
        id: 'footer.privacyPolicy',
        defaultMessage: 'Privacy Policy'
    },
    brandProtection: {
        id: 'footer.brandProtection',
        defaultMessage: 'Brand Protection'
    },
    promoDetails: {
        id: 'footer.promoDetails',
        defaultMessage: 'Promo Details'
    },
    accessibilityStatement: {
        id: 'footer.accessibilityStatement',
        defaultMessage: 'Accessibility Statement'
    },
    sitemap: {
        id: 'footer.sitemap',
        defaultMessage: 'Sitemap'
    }
})

const AccordionIcon = ({isExpanded}) => {
    const styles = useMultiStyleConfig('FooterNavigationLinks', {isExpanded})

    return <AccordionArrow {...styles.accordionIcon} />
}

// GTM Modification
const sendGTMEvent = (type) => {
    const normalized = type.toLowerCase().replace(/\s+/g, '-')
    sendClickEvent(`footer_link_${normalized}`)
}

AccordionIcon.propTypes = {
    isExpanded: PropTypes.bool
}

const NavigationLinks = ({footerData, ...otherProps}) => {
    const styles = useMultiStyleConfig('FooterNavigationLinks')
    const intl = useIntl()
    const getLinkData = useGetLinkData()
    const config = getConfig()

    /* Map CMS data to footer links structure */
    const NAV_LINKS_DATA = useMemo(() => {
        if (!footerData?.raw?.Items) {
            // Fallback to default data if CMS data is not available
            return [
                {
                    sectionId: 'aboutUS',
                    title: intl.formatMessage(messages.aboutUs),
                    links: [
                        {
                            label: intl.formatMessage(messages.aboutGarage),
                            href: '/',
                            id: messages.aboutGarage.id
                        },
                        ...(config.app?.STORELOCATOR_ENABLED === 'true'
                            ? [
                                  {
                                      label: intl.formatMessage(messages.storeLocator),
                                      href: '/stores',
                                      id: messages.storeLocator.id
                                  }
                              ]
                            : []),
                        {
                            label: intl.formatMessage(messages.careers),
                            href: '/',
                            id: messages.careers.id
                        },
                        {
                            label: intl.formatMessage(messages.communityPage),
                            href: '/',
                            id: messages.communityPage.id
                        },
                        {
                            label: intl.formatMessage(messages.cookiePreferences),
                            href: '/',
                            id: messages.cookiePreferences.id,
                            preventDefault: true,
                            onClick() {
                                if (typeof window !== 'undefined' && window.Didomi) {
                                    window.Didomi.preferences.show()
                                }
                            }
                        },
                        {
                            label: intl.formatMessage(messages.affiliateProgram),
                            href: '/',
                            id: messages.affiliateProgram.id
                        },
                        {
                            label: intl.formatMessage(messages.gscAmbassadorProgram),
                            href: '/',
                            id: messages.gscAmbassadorProgram.id
                        },
                        {
                            label: intl.formatMessage(messages.loyaltyProgram),
                            href: '/',
                            id: messages.loyaltyProgram.id
                        }
                    ]
                },
                {
                    sectionId: 'customerService',
                    title: intl.formatMessage(messages.customerService),
                    links: [
                        {
                            label: intl.formatMessage(messages.contactUs),
                            href: '/',
                            id: messages.contactUs.id
                        },
                        {
                            label: intl.formatMessage(messages.orderTracking),
                            href: '/',
                            id: messages.orderTracking.id
                        },
                        {
                            label: intl.formatMessage(messages.faq),
                            href: '/faq',
                            id: messages.faq.id
                        },
                        {
                            label: intl.formatMessage(messages.shippingDelivery),
                            href: '/',
                            id: messages.shippingDelivery.id
                        },
                        {
                            label: intl.formatMessage(messages.returnPolicy),
                            href: '/',
                            id: messages.returnPolicy.id
                        },
                        {
                            label: intl.formatMessage(messages.sizeGuide),
                            href: '/',
                            id: messages.sizeGuide.id
                        },
                        {
                            label: intl.formatMessage(messages.orderHistory),
                            href: '/',
                            id: messages.orderHistory.id
                        },
                        {
                            label: intl.formatMessage(messages.buyGiftCard),
                            href: '/',
                            id: messages.buyGiftCard.id
                        },
                        {
                            label: intl.formatMessage(messages.giftCardBalance),
                            href: '/',
                            id: messages.giftCardBalance.id
                        },
                        {
                            label: intl.formatMessage(messages.paymentInfo),
                            href: '/',
                            id: messages.paymentInfo.id
                        },
                        {
                            label: intl.formatMessage(messages.klarna),
                            href: '/',
                            id: messages.klarna.id
                        },
                        {
                            label: intl.formatMessage(messages.sitemap),
                            href: '/sitemap',
                            id: messages.sitemap.id
                        }
                    ]
                },
                {
                    sectionId: 'socials',
                    title: intl.formatMessage(messages.socials),
                    links: [
                        {
                            label: intl.formatMessage(messages.tiktok),
                            href: '/',
                            id: messages.tiktok.id
                        },
                        {
                            label: intl.formatMessage(messages.instagram),
                            href: '/',
                            id: messages.instagram.id
                        },
                        {
                            label: intl.formatMessage(messages.facebook),
                            href: '/',
                            id: messages.facebook.id
                        },
                        {
                            label: intl.formatMessage(messages.pinterest),
                            href: '/',
                            id: messages.pinterest.id
                        }
                    ]
                },
                {
                    sectionId: 'legals',
                    title: intl.formatMessage(messages.legals),
                    links: [
                        {
                            label: intl.formatMessage(messages.termsConditions),
                            href: '/',
                            id: messages.termsConditions.id
                        },
                        {
                            label: intl.formatMessage(messages.privacyPolicy),
                            href: '/',
                            id: messages.privacyPolicy.id
                        },
                        {
                            label: intl.formatMessage(messages.brandProtection),
                            href: '/',
                            id: messages.brandProtection.id
                        },
                        {
                            label: intl.formatMessage(messages.promoDetails),
                            href: '/',
                            id: messages.promoDetails.id
                        },
                        {
                            label: intl.formatMessage(messages.accessibilityStatement),
                            href: '/',
                            id: messages.accessibilityStatement.id
                        }
                    ]
                }
            ]
        }

        // Map CMS data to the expected structure
        return footerData.raw.Items.map((column, index) => {
            const expanded = column.Expanded
            if (!expanded) return null

            return {
                sectionId: `section-${index}`,
                title: expanded.Title || '',
                titleStyles: generateStyles(
                    expanded.WebFooterColumnBlockTitleStyle,
                    styles.sectionTitle
                ),
                itemStyles: generateStyles(expanded.ItemsStyleGlobalByColumn, styles.footerLink),
                links: (expanded.Items || [])
                    .map((item) => {
                        const itemExpanded = item.Expanded
                        if (!itemExpanded) return null

                        const linkData = getLinkData(itemExpanded.Link?.Expanded)
                        const label = itemExpanded.CtaLinkText || ''

                        // Check if this is a Store Locator link and if it should be shown
                        const isStoreLocator =
                            label.toLowerCase().includes('store') &&
                            label.toLowerCase().includes('locator')

                        if (isStoreLocator && config.app?.STORELOCATOR_ENABLED !== 'true') {
                            return null
                        }

                        // Check if this is a Cookie Preferences link
                        const isCookiePreferences =
                            label.toLowerCase().includes('cookie') &&
                            label.toLowerCase().includes('preference')

                        return {
                            label,
                            href: linkData.generatedURL,
                            isExternalUrl: linkData.isExternalUrl, // Flag to determine if we should use ChakraLink or React Router Link
                            ...(!linkData.stayOnSamePage && {
                                target: linkData?.target,
                                rel: linkData?.rel
                            }),
                            id: label, // Use label as ID for GTM tracking
                            preventDefault: itemExpanded.PreventDefault || isCookiePreferences,
                            onClick: isCookiePreferences
                                ? function () {
                                      if (typeof window !== 'undefined' && window.Didomi) {
                                          window.Didomi.preferences.show()
                                      }
                                  }
                                : itemExpanded.OnClick || null
                        }
                    })
                    .filter((link) => link && link.href)
            }
        }).filter(Boolean)
    }, [footerData, intl, styles.sectionTitle, styles.footerLink, getLinkData])

    return (
        <React.Fragment {...otherProps}>
            <HideOnDesktop as={Accordion} allowMultiple>
                {NAV_LINKS_DATA.map((section) => (
                    <AccordionItem key={section.sectionId} {...styles.accordionItem}>
                        {({isExpanded}) => (
                            <>
                                <AccordionButton
                                    data-testid={`footer-accordion-button-${section.sectionId}`}
                                    {...styles.accordionButton}
                                >
                                    <Text {...(section.titleStyles || styles.sectionTitle)}>
                                        {section.title}
                                    </Text>
                                    <AccordionIcon isExpanded={isExpanded} />
                                </AccordionButton>
                                <AccordionPanel
                                    {...(isExpanded
                                        ? styles.accordionPanelExpanded
                                        : styles.accordionPanel)}
                                >
                                    {section.links.map((link) => (
                                        <Box key={link.label} {...styles.linkContainer}>
                                            {link.isExternalUrl ? (
                                                <ChakraLink
                                                    href={link.href}
                                                    isExternal={link.target === '_blank'}
                                                    {...(link.target && {target: link.target})}
                                                    {...(link.rel && {rel: link.rel})}
                                                    {...(section.itemStyles || styles.footerLink)}
                                                    onClick={(e) => {
                                                        if (link.preventDefault) {
                                                            e.preventDefault()
                                                            if (link.onClick) {
                                                                link.onClick()
                                                            }
                                                        }
                                                        if (link.id) {
                                                            sendGTMEvent(link.id)
                                                        }
                                                    }}
                                                >
                                                    {link.label}
                                                </ChakraLink>
                                            ) : (
                                                <Link
                                                    to={link.href}
                                                    {...(section.itemStyles || styles.footerLink)}
                                                    onClick={(e) => {
                                                        if (link.preventDefault) {
                                                            e.preventDefault()
                                                            if (link.onClick) {
                                                                link.onClick()
                                                            }
                                                        }
                                                        if (link.id) {
                                                            sendGTMEvent(link.id)
                                                        }
                                                    }}
                                                >
                                                    {link.label}
                                                </Link>
                                            )}
                                        </Box>
                                    ))}
                                </AccordionPanel>
                            </>
                        )}
                    </AccordionItem>
                ))}
            </HideOnDesktop>
            {NAV_LINKS_DATA.map((section) => (
                <HideOnMobile key={section.sectionId}>
                    <Box>
                        <Text {...(section.titleStyles || styles.sectionTitle)}>
                            {section.title}
                        </Text>
                        {section.links.map((link) => (
                            <Box key={link.label} {...styles.linkContainer}>
                                {link.isExternalUrl ? (
                                    <ChakraLink
                                        href={link.href}
                                        isExternal={link.target === '_blank'}
                                        {...(link.target && {target: link.target})}
                                        {...(link.rel && {rel: link.rel})}
                                        {...(section.itemStyles || styles.footerLink)}
                                        onClick={(e) => {
                                            if (link.preventDefault) {
                                                e.preventDefault()
                                                if (link.onClick) {
                                                    link.onClick()
                                                }
                                            }
                                            if (link.id) {
                                                sendGTMEvent(link.id)
                                            }
                                        }}
                                    >
                                        {link.label}
                                    </ChakraLink>
                                ) : (
                                    <Link
                                        to={link.href}
                                        {...(section.itemStyles || styles.footerLink)}
                                        onClick={(e) => {
                                            if (link.preventDefault) {
                                                e.preventDefault()
                                                if (link.onClick) {
                                                    link.onClick()
                                                }
                                            }
                                            if (link.id) {
                                                sendGTMEvent(link.id)
                                            }
                                        }}
                                    >
                                        {link.label}
                                    </Link>
                                )}
                            </Box>
                        ))}
                    </Box>
                </HideOnMobile>
            ))}
        </React.Fragment>
    )
}

NavigationLinks.propTypes = {
    footerData: PropTypes.object,
    otherProps: PropTypes.object
}

export default NavigationLinks
